import type { ReactNode } from 'react';
import { cx } from './cx.js';

export interface EmptyStateProps {
  title: ReactNode;
  children?: ReactNode;
  /** A short decorative glyph. */
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/** What a list says when it has nothing yet, and the one thing to do about it. */
export function EmptyState({ title, children, icon = '＋', action, className }: EmptyStateProps) {
  return (
    <div className={cx('qk-empty', className)}>
      <span className="qk-empty__icon" aria-hidden="true">
        {icon}
      </span>
      <p className="qk-empty__title">{title}</p>
      {children ? <div className="qk-empty__text">{children}</div> : null}
      {action ? <div className="qk-empty__action">{action}</div> : null}
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Primary actions, at the end (wrap under the title on phones). */
  actions?: ReactNode;
  /** Small text above the title (the section it belongs to). */
  eyebrow?: ReactNode;
  className?: string;
}

/** The h1 of a screen with its description and main actions. */
export function PageHeader({ title, description, actions, eyebrow, className }: PageHeaderProps) {
  return (
    <header className={cx('qk-page-header', className)}>
      <div className="qk-page-header__text">
        {eyebrow ? <p className="qk-page-header__eyebrow">{eyebrow}</p> : null}
        <h1 className="qk-page-header__title">{title}</h1>
        {description ? <p className="qk-page-header__description">{description}</p> : null}
      </div>
      {actions ? <div className="qk-page-header__actions">{actions}</div> : null}
    </header>
  );
}

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export function Badge({
  tone = 'accent',
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return <span className={cx('qk-badge', `qk-badge--${tone}`, className)}>{children}</span>;
}
